// package imports
import React, { useCallback, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate, useSearchParams } from 'react-router-dom';

// CSS imports
import './NewsletterUsers.css';

// Component imports
import Spinner from '../../components/spinner/Spinner';
import Alert from '../../components/alert/Alert';
import SearchBar from '../../components/searchBar/SearchBar';

// Helper imports
import SectionWrapper from '../../helpers/components/sectionWrapper/SectionWrapper';

// Service imports
import { ApiService } from '../../services/ApiService';

// Icon imports
import { MdError } from 'react-icons/md';

// Type imports
import { SectionContentSize } from '../../types/sectionWrapper/SectionContentSize';
import { AlertSize } from '../../types/alert/AlertSize';
import { NewsletterUser } from '../../types/newsletter/NewsletterUser';
import { NewsletterUsersResult } from '../../types/newsletter/NewsletterUsersResult';
import NewsletterUserCard from '../../components/newsletterUserCard/NewsletterUserCard';

const NewsletterUsers: React.FC = () => {
  const [search, setSearch] = useState('');
  const [selectedSearch, setSelectedSearch] = useState('');
  const [selectedPage, setSelectedPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [users, setUsers] = useState<NewsletterUser[]>([]);

  const [showUnknownError, setShowUnknownError] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const getNewsletterUsers = useCallback(
    async (tempUsers: NewsletterUser[], search: string, page: number) => {
      const res: NewsletterUsersResult | null =
        await ApiService.getNewsletterUsers(search, page, 9);
      if (res) {
        for (const user of res.users) {
          tempUsers.push(user);
        }
        setUsers(tempUsers);
        setTotalCount(res.total);
        setSelectedPage(res.page);
        setPagesCount(res.pages);
      }
    },
    []
  );

  useEffect(() => {
    let urlSearch = searchParams.get('search');
    let finalSearch = urlSearch ? decodeURI(urlSearch) : '';

    setSelectedSearch(finalSearch);

    getNewsletterUsers([], finalSearch, 1);
  }, [searchParams, getNewsletterUsers]);

  const deleteUser = async (id: string, index: number) => {
    const res: boolean = await ApiService.unsubscribeNewsletter(id);
    if (res) {
      const tempUsers = users.slice(0, users.length);
      tempUsers.splice(index, 1);
      setUsers(tempUsers);
    } else {
      setShowUnknownError(true);
    }
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const changeSearch = async (newSearch: string) => {
    navigate(
      '/newsletter-users' +
        (newSearch !== '' ? '?search=' + encodeURI(newSearch) : '')
    );
  };

  return (
    <div className='NewsletterUsers'>
      <SectionWrapper contentWidth={SectionContentSize.MEDIUM}>
        <div className='content'>
          <h1>Newsletter Users</h1>
          <SearchBar
            className='search-bar'
            value={search}
            placeholder='Search'
            onChange={onSearchChange}
            triggerSearch={changeSearch}
          />
          <div className='user-count'>
            <span>{totalCount}</span>
            <span>Users</span>
          </div>
          <InfiniteScroll
            dataLength={users.length}
            hasMore={selectedPage !== pagesCount}
            next={() =>
              getNewsletterUsers(users, selectedSearch, selectedPage + 1)
            }
            loader={<Spinner isLoading={true} />}
            className='infinite-scroll-container'
          >
            {users.map((user: NewsletterUser, index: number) => (
              <NewsletterUserCard
                className='item-card'
                index={index}
                user={user}
                deleteUser={deleteUser}
                key={index}
              />
            ))}
          </InfiniteScroll>
          {/* {!isLoading && users.length === 0 ? (
            <span className='no-items-found'>No users found</span>
          ) : null} */}
        </div>
      </SectionWrapper>
      <Alert
        showAlert={showUnknownError}
        size={AlertSize.SMALL}
        time={3000}
        setShowAlert={setShowUnknownError}
      >
        <MdError size={20} />
        <span>Something went wrong!</span>
      </Alert>
    </div>
  );
};

export default NewsletterUsers;
